import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TenantUsersApi } from "../../../api/common/apiIndex";
import Loader from "../../common/commonUtils/Loader";
import useDebounce from "../hooks/useDebounce";
import { formatRole, roleBadgeClass, statusBadgeClass } from "./UserProfileCard";

const ROLE_FILTERS = [
  { label: "All roles", value: "ALL" },
  { label: "Owners", value: "BUSINESS_OWNER" },
  { label: "Admins", value: "ADMIN" },
  { label: "Agents", value: "AGENT" },
];

export default function TeamPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [size] = useState(15);
  const [sort] = useState("firstName,asc");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const debouncedSearch = useDebounce(search, 350);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, roleFilter]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["tenant-users", { page, size, sort, q: debouncedSearch, role: roleFilter }],
    queryFn: () =>
      TenantUsersApi.list({
        page,
        size,
        sort,
        q: debouncedSearch,
        role: roleFilter === "ALL" ? undefined : roleFilter,
      }),
    keepPreviousData: true,
  });

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;

  const rangeText = useMemo(() => {
    if (!totalElements) return "0–0 of 0";
    const start = page * size + 1;
    const end = Math.min(start + rows.length - 1, totalElements);
    return `${start}–${end} of ${totalElements}`;
  }, [page, size, rows.length, totalElements]);

  const handleNavigate = (row) => {
    const uuid = row?.uuid;
    if (!uuid) return;
    const roles = Array.isArray(row?.roles) ? row.roles : [];
    if (roles.includes("AGENT")) {
      navigate(`/home/agent/${uuid}`);
      return;
    }
    navigate(`/home/team/${uuid}`);
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h5 className="mb-1">Team</h5>
            <p className="mb-0 text-body-secondary">View everyone in your business and drill into the details.</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill"
              onClick={() => navigate("/home/create-user")}
            >
              <i className="bi bi-person-plus me-2" aria-hidden="true" />
              Add teammate
            </button>
          </div>
        </div>
      </div>

      <div className="card border shadow-sm rounded-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6">
              <label htmlFor="team-search" className="form-label text-body-secondary small">
                Search
              </label>
              <div className="input-group">
                <span className="input-group-text bg-body" id="search-icon">
                  <i className="bi bi-search" />
                </span>
                <input
                  id="team-search"
                  type="search"
                  className="form-control"
                  placeholder="Name or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-describedby="search-icon"
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label text-body-secondary small">Role</label>
              <div className="d-flex flex-wrap gap-2">
                {ROLE_FILTERS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`btn btn-sm rounded-pill ${
                      roleFilter === option.value ? "btn-primary" : "btn-outline-secondary"
                    }`}
                    onClick={() => setRoleFilter(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border shadow-sm rounded-4">
        <div className="card-body p-0">
          {isError && (
            <div className="alert alert-danger m-3 mb-0" role="alert">
              {error?.response?.data?.message || error?.message || "Failed to load team members."}
            </div>
          )}
          {(isLoading && !data) ? (
            <div className="py-5">
              <Loader text="Loading team…" />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Roles</th>
                    <th scope="col">Status</th>
                    <th scope="col">Country</th>
                    <th scope="col">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-body-secondary">
                        No users found.
                      </td>
                    </tr>
                  )}
                  {rows.map((user) => {
                    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || "—";
                    const created = user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—";
                    return (
                      <tr
                        key={user.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleNavigate(user)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleNavigate(user);
                          }
                        }}
                      >
                        <td>
                          <div className="fw-semibold">{fullName}</div>
                          <div className="text-body-secondary small">{user.email}</div>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {(user.roles || []).map((role, idx) => (
                              <span key={`${role}-${idx}`} className={roleBadgeClass(role)}>
                                {formatRole(role)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className={statusBadgeClass(user.active)}>
                            {user.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>{(user.country || "").toUpperCase() || "—"}</td>
                        <td>{created}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card-footer d-flex flex-wrap align-items-center justify-content-between gap-2">
          <small className="text-body-secondary">{rangeText}{isFetching ? " …" : ""}</small>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill"
              disabled={page === 0 || isFetching}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <span className="text-body-secondary small">
              Page {totalPages === 0 ? 0 : page + 1} / {totalPages}
            </span>
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill"
              disabled={page + 1 >= totalPages || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
